/**
 * DBLC Panel
 *
 * Renders the generated Database Lock Checklist inline within the
 * Study Documents page. The DBLC is structured as 19 ordered
 * categories of checklist items rather than narrative sections, so
 * the inline preview surfaces each category as a collapsible card
 * showing item count, applicable / N/A breakdown, and a per-item
 * table.
 */

import { GENERATION_LOCK_STALE_MINUTES, GENERATION_LOCK_STALE_MS } from '../../../../functions/src/utils/generationWindow';
import { useState, useEffect } from 'react';
import { onSnapshot } from 'firebase/firestore';
import { httpsCallable } from '../../lib/callable';
import { functions } from '../../firebase';
import { CLOUD_FUNCTIONS, CALLABLE_TIMEOUTS } from '../../constants/firebase';
import { dataManagementDoc } from '../../constants/firestorePaths';
import { useToast } from '../../contexts/ToastContext';
import DownloadButton from '../../features/downloads/DownloadButton';
import { useDocumentDownload } from '../../features/downloads/useDocumentDownload';
import { studyDocumentDownloadOptions } from '../../features/downloads/studyDocumentDownloads';
import {
  Loader2,
  Play,
  RefreshCw,
  ChevronDown,
  ChevronRight,
  Lock,
  CheckSquare,
  Square,
  ListChecks,
  FileSignature,
  Unlock,
  ShieldCheck,
} from 'lucide-react';
import { displayGenerationError } from '../../utils/generationFailureText';

/* ---------- Types ---------- */

interface DblcDoc {
  version?: number;
  status?: string;
  title?: string;
  generatedAt?: any;
  generatedBy?: string;
  studyInfo?: {
    studyTitle?: string;
    shortTitle?: string;
    protocolNumber?: string;
    sponsor?: string;
    phase?: string;
    edcSystem?: string;
  };
  generatedContent?: Record<string, any>;
  categoryCount?: number;
  totalItemCount?: number;
  applicableItemCount?: number;
  notApplicableItemCount?: number;
  signatureCount?: number;
  externalSourceCount?: number;
  submissionTargetCount?: number;
  _generating?: boolean;
  _generationStartedAt?: any;
  _generationStartedBy?: string;
  _generationType?: string;
  _generationError?: string;
  _generationFailedAt?: any;
}

interface DBLCPanelProps {
  projectId: string;
  /**
   * The Hub's in-flight state for this document, so ONE generation disables
   * EVERY control that can start it.
   *
   * This panel is rendered inside the Hub's dblc card, which carries its
   * own Regenerate button, so two controls for the same action sit on screen
   * together. Each used to hold only its own state. Both do read the server's
   * `_generating` marker, but that is set only after a round trip, so at the
   * instant of a click neither could see the other's — and on 2026-08-17 one
   * click on the MCS card produced two requests 0.67ms apart, the second
   * refused by the per-study lock and shown to the user as a failure while the
   * first was succeeding.
   *
   * The Hub owns the authoritative state (its synchronous `generatingDocs`
   * ORed with the same marker), so passing it down closes that window
   * immediately rather than a round trip later.
   */
  generating?: boolean;
}

const STATUS_STYLES: Record<string, string> = {
  draft: 'bg-gray-100 text-gray-600',
  'in-review': 'bg-blue-100 text-blue-700',
  approved: 'bg-green-100 text-green-700',
};

const PROGRESS_STEPS = [
  'Loading protocol and study setup...',
  'Reading data dictionary and detecting coding scope...',
  'Resolving external data sources...',
  'Authoring lock readiness thresholds...',
  'Building 19-category checklist...',
  'Tailoring conditional items to study scope...',
  'Composing approval signatory list...',
  'Drafting post-lock activities and unlock governance...',
  'Assembling final document...',
];

/* ---------- Inline preview helpers ---------- */

function ItemTable({ items }: { items: any[] }) {
  if (!Array.isArray(items) || items.length === 0) {
    return <p className="text-xs italic text-gray-400">(no items in this category)</p>;
  }
  return (
    <div className="overflow-x-auto border border-gray-200 rounded">
      <table className="min-w-full text-xs">
        <thead className="bg-gray-50">
          <tr>
            <th className="px-2 py-1.5 text-left font-semibold text-gray-600 w-6">#</th>
            <th className="px-2 py-1.5 text-left font-semibold text-gray-600">Checklist Item</th>
            <th className="px-2 py-1.5 text-left font-semibold text-gray-600 whitespace-nowrap">Owner</th>
            <th className="px-2 py-1.5 text-left font-semibold text-gray-600 whitespace-nowrap">Signatory</th>
            <th className="px-2 py-1.5 text-left font-semibold text-gray-600 whitespace-nowrap">Evidence</th>
            <th className="px-2 py-1.5 text-center font-semibold text-gray-600 w-12">Status</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-100">
          {items.map((it: any, idx: number) => {
            const applicable = it?.applicable !== false;
            const conditional = it?.conditional === true;
            return (
              <tr key={idx} className={applicable ? 'hover:bg-gray-50' : 'bg-gray-50 text-gray-400'}>
                <td className="px-2 py-1.5 align-top text-gray-500">{idx + 1}</td>
                <td className="px-2 py-1.5 align-top">
                  <div className="font-medium text-gray-800">{it?.text}</div>
                  {it?.acceptanceCriterion && (
                    <div className="text-[11px] text-gray-500 mt-0.5">
                      <span className="italic">Acceptance:</span> {it.acceptanceCriterion}
                    </div>
                  )}
                  {conditional && (
                    <div className={`text-[10px] mt-0.5 ${applicable ? 'text-blue-600' : 'text-gray-400'}`}>
                      {applicable ? 'Conditional - Applicable' : 'Conditional - N/A'}
                    </div>
                  )}
                  {!applicable && it?.notApplicableRationale && (
                    <div className="text-[11px] italic text-gray-400 mt-0.5">
                      N/A: {it.notApplicableRationale}
                    </div>
                  )}
                  {it?.notes && (
                    <div className="text-[11px] text-gray-500 mt-0.5">
                      <span className="italic">Note:</span> {it.notes}
                    </div>
                  )}
                </td>
                <td className="px-2 py-1.5 align-top text-gray-600 whitespace-nowrap">{it?.ownerRole}</td>
                <td className="px-2 py-1.5 align-top text-gray-600 whitespace-nowrap">{it?.signatoryRole}</td>
                <td className="px-2 py-1.5 align-top text-gray-500">{it?.evidenceType}</td>
                <td className="px-2 py-1.5 align-top text-center">
                  {applicable ? (
                    <Square className="h-3.5 w-3.5 text-gray-400 inline" />
                  ) : (
                    <span className="text-[10px] font-mono text-gray-400">N/A</span>
                  )}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

function ScopeNotesBlock({ scopeNotes, lockType, lockTypeNote }: { scopeNotes: any; lockType: string; lockTypeNote: string }) {
  if (!scopeNotes && !lockType) return null;
  return (
    <div className="space-y-3 bg-gray-50 border border-gray-200 rounded p-3">
      {scopeNotes?.purpose && <p className="text-xs text-gray-700">{scopeNotes.purpose}</p>}
      <div className="flex gap-3 text-xs">
        <span className="text-gray-500 font-medium">Lock Type:</span>
        <span className="text-gray-700 font-mono">{lockType}</span>
      </div>
      {lockTypeNote && (
        <p className="text-xs text-gray-600 italic">{lockTypeNote}</p>
      )}
      {Array.isArray(scopeNotes?.applicableScopeAttributes) && scopeNotes.applicableScopeAttributes.length > 0 && (
        <div>
          <p className="text-xs font-medium text-gray-600 mb-1">Applicable Scope</p>
          <ul className="list-disc ml-5 space-y-0.5 text-xs text-gray-700">
            {scopeNotes.applicableScopeAttributes.map((s: string, i: number) => <li key={i}>{s}</li>)}
          </ul>
        </div>
      )}
      {Array.isArray(scopeNotes?.excludedScopeAttributes) && scopeNotes.excludedScopeAttributes.length > 0 && (
        <div>
          <p className="text-xs font-medium text-gray-600 mb-1">Excluded / N/A</p>
          <ul className="list-disc ml-5 space-y-0.5 text-xs text-gray-500">
            {scopeNotes.excludedScopeAttributes.map((s: string, i: number) => <li key={i}>{s}</li>)}
          </ul>
        </div>
      )}
      {scopeNotes?.thresholds && (
        <div>
          <p className="text-xs font-medium text-gray-600 mb-1">Lock Readiness Thresholds</p>
          <table className="min-w-full text-xs">
            <tbody className="divide-y divide-gray-200">
              {[
                ['Query Closure', scopeNotes.thresholds.queryClosure],
                ['Coding Completion', scopeNotes.thresholds.codingCompletion],
                ['SDV / SDR', scopeNotes.thresholds.sdvSdr],
                ['Deviation Review', scopeNotes.thresholds.deviationReview],
                ['Audit Trail Review', scopeNotes.thresholds.auditTrailReview],
              ].map(([k, v]) => (
                <tr key={String(k)}>
                  <td className="py-1 pr-3 text-gray-500 font-medium align-top w-40">{k}</td>
                  <td className="py-1 text-gray-700 align-top">{v || <span className="italic text-gray-400">(unspecified)</span>}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function SignaturesBlock({ signatures }: { signatures: any[] }) {
  if (!Array.isArray(signatures) || signatures.length === 0) {
    return <p className="text-xs italic text-gray-400">(no signatures specified)</p>;
  }
  return (
    <div className="overflow-x-auto border border-gray-200 rounded">
      <table className="min-w-full text-xs">
        <thead className="bg-gray-50">
          <tr>
            <th className="px-2 py-1.5 text-left font-semibold text-gray-600">Role</th>
            <th className="px-2 py-1.5 text-left font-semibold text-gray-600">Function</th>
            <th className="px-2 py-1.5 text-left font-semibold text-gray-600">Mode</th>
            <th className="px-2 py-1.5 text-left font-semibold text-gray-600">Required</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-100">
          {signatures.map((s: any, i: number) => {
            const conditional = s?.conditional === true;
            const required = s?.required !== false;
            return (
              <tr key={i} className="hover:bg-gray-50">
                <td className="px-2 py-1.5 text-gray-800 font-medium">
                  {s?.role}
                  {conditional && <span className="ml-1 text-[10px] text-blue-600">(conditional)</span>}
                </td>
                <td className="px-2 py-1.5 text-gray-600">{s?.function}</td>
                <td className="px-2 py-1.5 text-gray-600 font-mono text-[11px]">{s?.signatureMode || 'electronic'}</td>
                <td className="px-2 py-1.5 text-gray-600">{required ? 'Yes' : 'Optional'}</td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

/* ---------- Component ---------- */

export default function DBLCPanel({ projectId, generating: hubGenerating = false }: DBLCPanelProps) {
  const [dblcDoc, setDblcDoc] = useState<DblcDoc | null>(null);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [generationProgress, setGenerationProgress] = useState('');
  // Shares its running state with the card's Download button (same group).
  const download = useDocumentDownload(projectId, 'dblc');
  const [expandedCategoryId, setExpandedCategoryId] = useState<string | null>(null);
  const [showSignatures, setShowSignatures] = useState(false);
  const [showPostLock, setShowPostLock] = useState(false);
  const [showUnlock, setShowUnlock] = useState(false);
  const { showToast } = useToast();

  const STALE_GENERATION_MS = GENERATION_LOCK_STALE_MS;

  const isGeneratingFromMarker = (() => {
    if (!dblcDoc?._generating) return false;
    const startedAt = dblcDoc._generationStartedAt;
    if (!startedAt?.toDate) return true;
    const ageMs = Date.now() - startedAt.toDate().getTime();
    return ageMs < STALE_GENERATION_MS;
  })();

  const isStaleGeneration = (() => {
    if (!dblcDoc?._generating) return false;
    const startedAt = dblcDoc._generationStartedAt;
    if (!startedAt?.toDate) return false;
    return Date.now() - startedAt.toDate().getTime() >= STALE_GENERATION_MS;
  })();

  // Folds in the Hub's in-flight state (the shared guard — see
  // DBLCPanelProps.generating) alongside the local fast path and the Firestore
  // marker, so a click on the card's Regenerate disables this one at once.
  const showGenerating = generating || hubGenerating || (isGeneratingFromMarker && !isStaleGeneration);

  useEffect(() => {
    if (!projectId) return;
    const unsubscribe = onSnapshot(
      dataManagementDoc(projectId, 'dblc'),
      (snapshot) => {
        if (snapshot.exists()) {
          setDblcDoc(snapshot.data() as DblcDoc);
        } else {
          setDblcDoc(null);
        }
        setLoading(false);
      },
      (err) => {
        console.error('Error loading DBLC:', err);
        setLoading(false);
      },
    );
    return () => unsubscribe();
  }, [projectId]);

  const handleGenerate = async () => {
    setGenerating(true);
    setGenerationProgress(PROGRESS_STEPS[0]);
    let step = 0;
    const progressTimer = setInterval(() => {
      step++;
      if (step < PROGRESS_STEPS.length) {
        setGenerationProgress(PROGRESS_STEPS[step]);
      }
    }, 12000);

    try {
      const generateFn = httpsCallable(functions, CLOUD_FUNCTIONS.GENERATE_DATABASE_LOCK_CHECKLIST_V2, {
        timeout: CALLABLE_TIMEOUTS.GENERATOR_XLARGE,
      });
      await generateFn({ projectId });
    } catch (err: any) {
      console.error('DBLC generation error:', err);
      showToast(err.message || 'Failed to generate Database Lock Checklist', 'error');
    } finally {
      clearInterval(progressTimer);
      setGenerating(false);
      setGenerationProgress('');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-6 w-6 animate-spin text-gray-400" />
      </div>
    );
  }

  const hasRealContent = !!(dblcDoc && (dblcDoc.version || dblcDoc.title));

  if (!dblcDoc || !hasRealContent) {
    return (
      <div className="space-y-4">
        <div className="text-center py-8">
          <p className="text-gray-500 mb-2">
            Generate a study-tailored Database Lock Checklist with 19 ordered categories
            of pre-lock activities, conditional items resolved against your study scope
            (coding, external feeds, blinding, submission targets), and a sponsor-level
            signable approval block.
          </p>
          <p className="text-xs text-gray-400 mb-6">
            Content is generated from your protocol, study setup, and data dictionary,
            grounded in SCDM GCDMP, ICH E6(R3), 21 CFR Part 11, and CDISC submission
            standards.
          </p>

          {dblcDoc?._generationError && !dblcDoc?._generating && (
            <div className="text-xs text-red-700 bg-red-50 border border-red-200 rounded-lg p-2 mb-3">
              <strong>Previous generation failed:</strong> {displayGenerationError(dblcDoc._generationError)}
            </div>
          )}

          {isStaleGeneration && (
            <div className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-lg p-2 mb-3">
              A previous generation appears to have stalled (started more than {GENERATION_LOCK_STALE_MINUTES} minutes ago).
              You can try generating again.
            </div>
          )}

          {showGenerating ? (
            <div className="space-y-3">
              <p className="text-sm font-medium text-gray-700">
                {generating ? 'Generating...' : 'Generation in progress in the background...'}
              </p>
              <div className="flex items-center justify-center gap-2 text-sm text-blue-600">
                <Loader2 className="h-4 w-4 animate-spin" />
                {generating ? (generationProgress || 'Generating...') : 'Please wait...'}
              </div>
              <div className="max-w-xs mx-auto">
                <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
                  <div className="h-full bg-blue-500 rounded-full animate-pulse w-2/3" />
                </div>
              </div>
              <p className="text-xs text-gray-400">This may take 2-4 minutes.</p>
            </div>
          ) : (
            <button
              onClick={handleGenerate}
              className="inline-flex items-center gap-2 px-6 py-2.5 bg-slate-700 text-white rounded-lg hover:bg-slate-800 transition-colors font-medium"
            >
              <Play className="h-4 w-4" />
              Generate Database Lock Checklist
            </button>
          )}
        </div>
      </div>
    );
  }

  const status = dblcDoc.status || 'draft';
  const statusStyle = STATUS_STYLES[status] || STATUS_STYLES.draft;
  const content = dblcDoc.generatedContent || {};
  const categories: any[] = Array.isArray(content?.categories)
    ? [...content.categories].sort((a, b) => (a?.number ?? 0) - (b?.number ?? 0))
    : [];
  const dc = content?.documentControl || {};

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between bg-white border border-gray-200 rounded-lg px-4 py-3 flex-wrap gap-y-2">
        <div className="flex items-center gap-3 flex-wrap">
          <h2 className="text-lg font-semibold text-gray-800">Database Lock Checklist</h2>
          <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${statusStyle}`}>
            {status === 'in-review' ? 'In Review' : status.charAt(0).toUpperCase() + status.slice(1)}
          </span>
          <span className="text-xs text-gray-400 ml-2">
            {dblcDoc.categoryCount ?? 0} categories
            {' · '}
            {dblcDoc.totalItemCount ?? 0} items ({dblcDoc.applicableItemCount ?? 0} applicable, {dblcDoc.notApplicableItemCount ?? 0} N/A)
            {' · '}
            {dblcDoc.signatureCount ?? 0} signatures
            {(dblcDoc.externalSourceCount ?? 0) > 0 && <>{' · '}{dblcDoc.externalSourceCount} ext sources</>}
            {(dblcDoc.submissionTargetCount ?? 0) > 0 && <>{' · '}{dblcDoc.submissionTargetCount} submission target(s)</>}
          </span>
        </div>

        <div className="flex items-center gap-2">
          <DownloadButton
            download={download}
            options={studyDocumentDownloadOptions('dblc', { projectId })}
            variant="secondary"
            size="sm"
          />
        </div>
      </div>

      {dblcDoc?._generationError && !dblcDoc?._generating && (
        <div className="text-xs text-red-700 bg-red-50 border border-red-200 rounded-lg p-2">
          <strong>Previous generation failed:</strong> {displayGenerationError(dblcDoc._generationError)}
        </div>
      )}

      {isStaleGeneration && (
        <div className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-lg p-2">
          A previous generation appears to have stalled (started more than {GENERATION_LOCK_STALE_MINUTES} minutes ago).
        </div>
      )}

      {showGenerating && (
        <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
          <p className="text-sm font-medium text-blue-700 mb-1">
            {generating ? 'Regenerating...' : 'Generation in progress in the background...'}
          </p>
          <div className="flex items-center gap-2 text-sm text-blue-700">
            <Loader2 className="h-4 w-4 animate-spin" />
            {generating ? (generationProgress || 'Generating...') : 'Please wait...'}
          </div>
          <div className="mt-2 max-w-xs">
            <div className="h-2 bg-blue-100 rounded-full overflow-hidden">
              <div className="h-full bg-blue-500 rounded-full animate-pulse w-2/3" />
            </div>
          </div>
        </div>
      )}

      {/* Scope notes */}
      <ScopeNotesBlock scopeNotes={content?.scopeNotes} lockType={dc?.lockType || 'single'} lockTypeNote={dc?.lockTypeNote || ''} />

      {/* Categorized checklist */}
      <div className="space-y-2">
        <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider mt-2">Lock Readiness Checklist</p>
        {categories.map((cat: any) => {
          const items = Array.isArray(cat?.items) ? cat.items : [];
          const applicable = items.filter((i: any) => i?.applicable !== false).length;
          const na = items.length - applicable;
          const isExpanded = expandedCategoryId === cat?.id;
          return (
            <div key={cat?.id || cat?.number} className="border border-gray-200 rounded-lg overflow-hidden bg-white">
              <button
                onClick={() => setExpandedCategoryId(isExpanded ? null : cat?.id)}
                className="w-full flex items-center gap-3 px-4 py-3 text-left hover:bg-gray-50 transition-colors"
              >
                {isExpanded ? (
                  <ChevronDown className="h-4 w-4 text-gray-400 flex-shrink-0" />
                ) : (
                  <ChevronRight className="h-4 w-4 text-gray-400 flex-shrink-0" />
                )}
                <span className="text-xs font-mono text-gray-400 w-8">{cat?.number}</span>
                <span className="text-slate-600">
                  <ListChecks className="h-4 w-4" />
                </span>
                <span className="text-sm font-medium text-gray-800 flex-1">{cat?.title}</span>
                <span className="text-[11px] text-gray-500 whitespace-nowrap">
                  {items.length} items
                  {na > 0 && <span className="text-gray-400"> ({applicable} applicable, {na} N/A)</span>}
                </span>
              </button>
              {isExpanded && (
                <div className="px-4 pb-4 pt-1 ml-11 max-h-[60vh] overflow-y-auto">
                  {cat?.description && (
                    <p className="text-xs text-gray-600 mb-2">{cat.description}</p>
                  )}
                  <ItemTable items={items} />
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Approval signatures */}
      <div className="border border-gray-200 rounded-lg overflow-hidden bg-white">
        <button
          onClick={() => setShowSignatures(!showSignatures)}
          className="w-full flex items-center gap-3 px-4 py-3 text-left hover:bg-gray-50 transition-colors"
        >
          {showSignatures ? <ChevronDown className="h-4 w-4 text-gray-400" /> : <ChevronRight className="h-4 w-4 text-gray-400" />}
          <FileSignature className="h-4 w-4 text-slate-600" />
          <span className="text-sm font-medium text-gray-800 flex-1">Approval Signatures</span>
          <span className="text-[11px] text-gray-500">{(content?.approvalSignatures || []).length} signatories</span>
        </button>
        {showSignatures && (
          <div className="px-4 pb-4 pt-1 ml-11">
            <SignaturesBlock signatures={content?.approvalSignatures || []} />
          </div>
        )}
      </div>

      {/* Post-lock activities */}
      <div className="border border-gray-200 rounded-lg overflow-hidden bg-white">
        <button
          onClick={() => setShowPostLock(!showPostLock)}
          className="w-full flex items-center gap-3 px-4 py-3 text-left hover:bg-gray-50 transition-colors"
        >
          {showPostLock ? <ChevronDown className="h-4 w-4 text-gray-400" /> : <ChevronRight className="h-4 w-4 text-gray-400" />}
          <Lock className="h-4 w-4 text-slate-600" />
          <span className="text-sm font-medium text-gray-800 flex-1">Post-Lock Activities</span>
          <span className="text-[11px] text-gray-500">{(content?.postLockActivities?.items || []).length} items</span>
        </button>
        {showPostLock && (
          <div className="px-4 pb-4 pt-1 ml-11 space-y-2">
            {content?.postLockActivities?.introduction && (
              <p className="text-xs text-gray-600">{content.postLockActivities.introduction}</p>
            )}
            <div className="overflow-x-auto border border-gray-200 rounded">
              <table className="min-w-full text-xs">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-2 py-1.5 text-left font-semibold text-gray-600">Activity</th>
                    <th className="px-2 py-1.5 text-left font-semibold text-gray-600">Owner</th>
                    <th className="px-2 py-1.5 text-left font-semibold text-gray-600">Timing</th>
                    <th className="px-2 py-1.5 text-left font-semibold text-gray-600">Evidence</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {(content?.postLockActivities?.items || []).map((it: any, i: number) => (
                    <tr key={i} className="hover:bg-gray-50">
                      <td className="px-2 py-1.5 text-gray-800">{it?.activity}</td>
                      <td className="px-2 py-1.5 text-gray-600">{it?.owner}</td>
                      <td className="px-2 py-1.5 text-gray-600">{it?.timing}</td>
                      <td className="px-2 py-1.5 text-gray-500">{it?.evidence}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>

      {/* Unlock governance */}
      <div className="border border-gray-200 rounded-lg overflow-hidden bg-white">
        <button
          onClick={() => setShowUnlock(!showUnlock)}
          className="w-full flex items-center gap-3 px-4 py-3 text-left hover:bg-gray-50 transition-colors"
        >
          {showUnlock ? <ChevronDown className="h-4 w-4 text-gray-400" /> : <ChevronRight className="h-4 w-4 text-gray-400" />}
          <Unlock className="h-4 w-4 text-slate-600" />
          <span className="text-sm font-medium text-gray-800 flex-1">Unlock Governance</span>
        </button>
        {showUnlock && (
          <div className="px-4 pb-4 pt-1 ml-11 space-y-2">
            {content?.unlockGovernance?.introduction && (
              <p className="text-xs text-gray-700">{content.unlockGovernance.introduction}</p>
            )}
            {Array.isArray(content?.unlockGovernance?.triggerExamples) && content.unlockGovernance.triggerExamples.length > 0 && (
              <div>
                <p className="text-xs font-medium text-gray-600 mb-1">Trigger Examples</p>
                <ul className="list-disc ml-5 space-y-0.5 text-xs text-gray-700">
                  {content.unlockGovernance.triggerExamples.map((t: string, i: number) => <li key={i}>{t}</li>)}
                </ul>
              </div>
            )}
            {Array.isArray(content?.unlockGovernance?.approvalChain) && content.unlockGovernance.approvalChain.length > 0 && (
              <div>
                <p className="text-xs font-medium text-gray-600 mb-1">Approval Chain</p>
                <ol className="list-decimal ml-5 space-y-0.5 text-xs text-gray-700">
                  {content.unlockGovernance.approvalChain.map((s: string, i: number) => <li key={i}>{s}</li>)}
                </ol>
              </div>
            )}
          </div>
        )}
      </div>

      <div className="flex justify-end">
        <button
          onClick={() => {
            if (
              confirm(
                'Regenerate the entire Database Lock Checklist with the latest study data? Existing content will be overwritten.',
              )
            ) {
              handleGenerate();
            }
          }}
          disabled={showGenerating}
          className="flex items-center gap-1.5 px-3 py-1.5 text-xs text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded transition-colors disabled:opacity-50"
        >
          <RefreshCw className={`h-3.5 w-3.5 ${showGenerating ? 'animate-spin' : ''}`} />
          Regenerate
        </button>
      </div>
    </div>
  );
}
